"use client"

import React, { useMemo, useState } from "react"
import { FacebookShareButton, FacebookIcon, LinkedinShareButton, LinkedinIcon, TwitterShareButton } from "react-share"
import { IoIosLink } from "react-icons/io"
import { FaXTwitter } from "react-icons/fa6"

type SocialsProps = {
   /** Article title */
   title?: string
   /** Meta-description */
   description?: string
   /** Keywords / tags – array or CSV string */
   keywords?: string | string[]
   /** Canonical URL (optional). Defaults to current page in the browser. */
   url?: string
}

export default function Socials({ title, description, keywords = [], url }: SocialsProps) {
   /* ─────────────────────────── 1 · SHARE URL ─────────────────────────── */
   //TO TEST LOCALLY, UNCOMMENT THIS:
   // const shareUrl = 'https://restaurantassociation.com';

   const shareUrl = url ?? (typeof window !== "undefined" ? window.location.href : "https://restaurantassociation.com")

   /* ─────────────── 2 · base hashtags from env (with safe fallback) ─────────────── */
   const baseHashtags = useMemo(() => {
      const raw = process.env.NEXT_PUBLIC_BASE_HASHTAGS ?? "restaurantassociation,news,food" // < fallback
      return raw
         .split(",")
         .map((t) => t.trim().replace(/^#/, ""))
         .filter(Boolean)
   }, [])

   /* ─────────────── 3 · normalise `keywords` prop to array<string> ─────────────── */
   const keywordArray = useMemo<string[]>(() => {
      if (Array.isArray(keywords)) return keywords
      if (typeof keywords === "string")
         return keywords
            .split(",")
            .map((t) => t.trim())
            .filter(Boolean)
      return []
   }, [keywords])

   /* ─────────────── 4 · merge & dedupe tags →  ['restaurantassociation','news',…] ─────────────── */
   const tags = useMemo(() => Array.from(new Set([...baseHashtags, ...keywordArray])), [baseHashtags, keywordArray])

   /* ─────────────── 5 · platform-specific pre-formatting ─────────────── */
   /** Facebook wants **one** hashtag string *with* the # symbol. */
   // 1. Pretty version (for displaying or logging)
   const fbHashtagPretty = tags.map((t) => `#${t}`).join("\n")

   /** Twitter hashtags: array *without* # */
   const twitterHashtags = tags

   /** Optional “related” accounts from env (comma-separated, no @) */
   const relatedAccounts = useMemo(() => {
      const raw = process.env.NEXT_PUBLIC_TWITTER_RELATED ?? ""
      return raw
         .split(",")
         .map((t) => t.trim().replace(/^@/, ""))
         .filter(Boolean)
   }, [])

   /** LinkedIn “source” (the site) – just hostname of the URL */
   const linkedInSource = useMemo(() => {
      try {
         return new URL(shareUrl).hostname.replace(/^www\./, "")
      } catch {
         return "restaurantassociation.com"
      }
   }, [shareUrl])

   /* ─────────────── 6 · Copy-link feedback ─────────────── */
   const [copied, setCopied] = useState(false)
   const handleCopy = async () => {
      try {
         await navigator.clipboard.writeText(shareUrl)
         setCopied(true)
         setTimeout(() => setCopied(false), 2000)
      } catch (e) {
         console.error("Clipboard write failed", e)
      }
   }

   /* ─────────────── 7 · shared hover / focus utilities ─────────────── */
   const hoverStyle =
      "transition-all duration-150 hover:scale-110 hover:opacity-80 focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-blue-500"

   return (
      <div className="flex w-full flex-row-reverse">
         <div className="flex items-center space-x-4">
            {/* Copy-link */}
            <button
               onClick={handleCopy}
               aria-label="Copy page link"
               className={`flex h-8 w-8 items-center justify-center rounded-full bg-gray-200 ${hoverStyle}`}
            >
               {copied ? <span className="text-xs font-semibold">✓</span> : <IoIosLink size={18} />}
            </button>

            {/* Facebook – only url & hashtag actually matter to FB */}
            <FacebookShareButton
               url={shareUrl}
               hashtag={fbHashtagPretty} // ← must be a single hashtag
               className={hoverStyle}
               aria-label="Share on Facebook"
            >
               <FacebookIcon size={32} round />
            </FacebookShareButton>

            {/* X / Twitter */}
            <TwitterShareButton
               url={shareUrl}
               title={title}
               hashtags={twitterHashtags}
               related={relatedAccounts}
               className={hoverStyle}
               aria-label="Share on Twitter"
            >
               <div className="flex h-8 w-8 items-center justify-center rounded-full bg-black">
                  <FaXTwitter size={20} className="text-white" />
               </div>
            </TwitterShareButton>

            {/* LinkedIn */}
            <LinkedinShareButton
               url={shareUrl}
               title={title}
               summary={description}
               source={linkedInSource}
               className={hoverStyle}
               aria-label="Share on LinkedIn"
            >
               <LinkedinIcon size={32} round />
            </LinkedinShareButton>
         </div>
      </div>
   )
}
